import {convertPriceToInvoiceCurrency} from '../../../components/insights/invoicing/line-generation/invoicing_generate_lines_utils';
import {workDetailProject} from '../../../components/insights/invoicing/line-generation/invoicing_work_detail_rows';
import type {WorkDetailTimeReg} from '../../../components/insights/invoicing/line-generation/invoicing_work_detail_rows';

/**
 * Builds the price converter the work-detail engine needs, resolving each registration's source
 * currency from the rate card of the project it was logged against.
 *
 * A time registration's `price` is denominated in its project's rate-card currency, but every amount
 * printed in the work-detail section is labelled with the invoice's currency symbol. The
 * single-invoice flow can put several projects on one invoice, so the source currency is resolved per
 * registration rather than once for the whole document.
 *
 * The fallback chain is the one used everywhere else in invoicing (getUnitPrice in
 * RetainerPeriodUtil, getAllTimeRegs in invoicing_generate_lines_timeregs): the project's rate-card
 * currency, then the company's.
 */

/**
 * Company fields the conversion reads: its own currency and its exchange-rate history.
 *
 * Absent as well as null, because callers read this straight off a Relay fragment, where an optional
 * field is `T | null | undefined`. Both mean the same thing here — no rates to convert with — and the
 * converter already returns the amount untouched in that case, so narrowing at each call site would
 * only push an assertion outwards for no gain.
 */
type WorkDetailCompany =
	| {
			currency?: string | null;
	  }
	| null
	| undefined;

/**
 * The invoice being built. `currency` is the conversion target; `date` selects the exchange rate
 * effective on that date, so an invoice raised today and one backdated a year can convert the same
 * work differently.
 */
type WorkDetailInvoice =
	| {
			currency?: string | null;
	  }
	| null
	| undefined;

/**
 * @param fallbackCurrency Source currency for callers whose query does not select the registration's
 *   own project rate card — a single-project flow, where every registration shares one. Defaults to
 *   the company's currency.
 */
export const workDetailPriceConverter = (
	company: WorkDetailCompany,
	invoice: WorkDetailInvoice,
	fallbackCurrency?: string | null
): ((price: number, timeReg: WorkDetailTimeReg) => number) => {
	const fallback = fallbackCurrency || company?.currency || '';

	return (price, timeReg) => {
		// No target currency, or no company to read an exchange rate off, so there is nothing to
		// convert with. The amount is left as it is rather than run through a conversion that has no
		// rates behind it.
		if (!invoice || !invoice.currency || !company) return price;
		// Resolved rather than read off the registration directly: task-logged time carries its project
		// on the task, and reading only the registration's own project silently converted every such
		// amount from the company currency instead of the rate card's.
		const sourceCurrency = workDetailProject(timeReg)?.rateCard?.currency || fallback;
		return convertPriceToInvoiceCurrency(price, sourceCurrency, company, invoice);
	};
};
